'use client';

import { useLocale } from 'next-intl';
import type { HeaderNavigationItem } from '@/ui/components/Header/header-types';
import AccountLink from '@/ui/components/AccountLink/AccountLink';
import Button from '@/ui/components/Button/Button';
import LanguageChanger from '@/ui/components/LanguageChanger/LanguageChanger';
import Typography from '@/ui/components/Typography/Typography';
import VectorGraphic from '@/ui/components/VectorGraphic/VectorGraphic';
import { useGA4 } from '@/ui/context/ga4';
import { useMobileMenu } from '@/ui/context/mobile-menu';
import useBrowseOnlyBookingAccountAccess from '@/ui/hooks/use-browse-only-booking-account-access';

import $ from './MobileMenuItems.module.scss';

type MobileMenuItemsProps = {
  /** Navigation items from Uniform (passed from Header). */
  navigationItems?: HeaderNavigationItem[];
};

const MobileMenuItems = ({ navigationItems = [] }: MobileMenuItemsProps) => {
  const locale = useLocale();
  const { closeMenu } = useMobileMenu();
  const { trackMenuNavigation } = useGA4();
  const { showHeaderAccountLink, accountEntryHref } =
    useBrowseOnlyBookingAccountAccess();

  const handleClick = (item: HeaderNavigationItem) => {
    trackMenuNavigation({
      menu: 'header',
      menu_path: item.path,
      menu_text: item.trackTitle ?? item.label,
      menu_click: 'text',
    });

    closeMenu();
  };

  return (
    <div className={$.wrapper}>
      <div className={$.menuContent}>
        <ul className={$.menuList}>
          {navigationItems.map(item => (
            <li key={item.path} className={$.menuItem}>
              <Button
                href={item.path}
                variant="tertiary"
                className={$.menuButton}
                onClick={() => handleClick(item)}
              >
                <Typography variant="bodyLarge" className={$.text}>
                  {item.label}
                </Typography>
                <VectorGraphic name="icon-chevron-right" className={$.arrow} />
              </Button>
            </li>
          ))}
          {showHeaderAccountLink && (
            <li className={$.menuItem}>
              <Button
                variant="tertiary"
                className={$.menuButton}
                onClick={closeMenu}
              >
                <AccountLink isMobile href={accountEntryHref} />
                <VectorGraphic name="icon-chevron-right" className={$.arrow} />
              </Button>
            </li>
          )}
        </ul>
      </div>

      <div className={$.footer}>
        <div className={$.languageContainer}>
          <LanguageChanger locale={locale} />
          <VectorGraphic
            name="icon-chevron-right"
            className={$.languageArrow}
          />
        </div>
      </div>
    </div>
  );
};

export default MobileMenuItems;
